package com.banyan.flightviewer.web

import com.banyan.flightviewer.web.components.mountAircraftTable
import com.banyan.flightviewer.web.components.mountAirportPanel
import com.banyan.flightviewer.web.components.mountAtcPlayerBar
import com.banyan.flightviewer.web.components.mountBookmarksPanel
import com.banyan.flightviewer.web.components.mountDetailPanel
import com.banyan.flightviewer.web.components.mountFilterPanel
import com.banyan.flightviewer.web.components.mountMapView
import com.banyan.flightviewer.web.components.mountStatusBar
import com.banyan.flightviewer.web.components.mountTopBar
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.dom.removeAllChildren
import com.banyan.flightviewer.web.state.WebAppContainer
import org.w3c.dom.Element

/**
 * Top-level layout:
 *
 *   ┌─ TopBar ─────────────────────────────┐
 *   │ FlightViewer    count   Settings     │
 *   ├──────────────────┬───────────────────┤
 *   │                  │ DetailPanel       │
 *   │      MapView     ├───────────────────┤
 *   │                  │ AirportPanel      │
 *   │                  ├───────────────────┤
 *   │                  │ Bookmarks         │
 *   │                  │ AircraftTable     │
 *   ├──────────────────┴───────────────────┤
 *   │ AtcPlayerBar (when streaming)        │
 *   │ StatusBar                            │
 *   │ Bottom nav (mobile portrait only)    │
 *   └──────────────────────────────────────┘
 */
fun mountApp(container: Element, app: WebAppContainer) {
    container.removeAllChildren()

    val rootEl = container.div(className = "fv-app") {
        mountTopBar(this, app)
        div(className = "fv-app__main") {
            mountMapView(this, app)
            div(className = "fv-sidebar") {
                div(className = "fv-sidebar__pane") {
                    mountDetailPanel(this, app)
                    mountAirportPanel(this, app)
                    mountBookmarksPanel(this, app)
                    mountFilterPanel(this, app)
                    mountAircraftTable(this, app)
                }
            }
        }
        mountAtcPlayerBar(this, app)
        mountStatusBar(this, app)
        mountBottomNav(this)
    }
    rootEl.setAttribute("data-mobile-tab", "map")

    // Expose "is an aircraft or airport currently selected?" as an
    // attribute on the root so mobile CSS can surface DetailPanel /
    // AirportPanel as a bottom-sheet overlay on top of the map. On
    // desktop the sidebar is always visible so this attribute is
    // ignored. (Bookmarks use a different pattern — tapping the ★ icon
    // flips the mobile tab to "list", handled in TopBar.kt.)
    fun refreshSelectionAttr() {
        val hasSelection = app.appState.selectedCallsign.value != null ||
            app.appState.selectedAirport.value != null
        rootEl.setAttribute("data-has-selection", if (hasSelection) "true" else "false")
    }
    refreshSelectionAttr()
    app.appState.scope.bindFlow(app.appState.selectedCallsign) { refreshSelectionAttr() }
    app.appState.scope.bindFlow(app.appState.selectedAirport) { refreshSelectionAttr() }
}

private fun mountBottomNav(parent: Element) {
    parent.div(className = "fv-bottomnav") {
        listOf("map" to "Map", "list" to "List").forEach { (key, label) ->
            button(className = "fv-bottomnav__btn") {
                textContent = label
                setAttribute("data-tab", key)
                onClick {
                    val app = parent.closest(".fv-app") ?: return@onClick
                    app.setAttribute("data-mobile-tab", key)
                    val all = app.querySelectorAll(".fv-bottomnav__btn")
                    for (i in 0 until all.length) {
                        val btn = all.item(i) as? Element ?: continue
                        val tab = btn.getAttribute("data-tab")
                        btn.className = if (tab == key) "fv-bottomnav__btn active" else "fv-bottomnav__btn"
                    }
                    // Force Leaflet to recompute viewport when switching to map
                    if (key == "map") {
                        kotlinx.browser.window.dispatchEvent(org.w3c.dom.events.Event("resize"))
                    }
                }
            }
        }
    }
    // Default-active button (map)
    val firstBtn = parent.querySelector(".fv-bottomnav__btn")
    firstBtn?.className = "fv-bottomnav__btn active"
}
